import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { graphql, useStaticQuery } from 'gatsby';

import { useConsentContext } from '~/context/ConsentContext';

import Button from '~/components/base/Button';
import BaseLink from '~/components/base/BaseLink';

import { p as pStyle, h4 } from '~/styles/typographyStyles';

import { getPathFromUrl } from '~/utilities/helpers';

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background-color: ${({ theme }) => theme.palette.black}99;
    z-index: ${({ theme }) => theme.zIndex.aboveHeader};
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
`;

const Modal = styled.div`
    background-color: ${({ theme }) => theme.palette.white};
    border-radius: 8px;
    width: 100%;
    max-width: 560px;
    box-shadow: 0 8px 40px ${({ theme }) => theme.palette.black}66;
`;

const ModalHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 24px 28px 20px;
    border-bottom: 1px solid ${({ theme }) => theme.palette.blueWhite};
`;

const ModalTitle = styled.h2`
    ${h4};
    margin: 0;
    color: ${({ theme }) => theme.palette.darkBlue};
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    padding: 4px 8px;
    cursor: pointer;
    color: ${({ theme }) => theme.palette.darkBlue};
    font-size: 2.4rem;
    line-height: 1;
    border-radius: 4px;
    transition: color ${({ theme }) => theme.animation.duration.fast}
        ease-in-out;

    &:hover,
    &:focus {
        color: ${({ theme }) => theme.palette.mediumBlue};
    }

    &:focus-visible {
        outline: 2px solid ${({ theme }) => theme.palette.mediumBlue};
        outline-offset: 2px;
    }
`;

const ModalBody = styled.div`
    padding: 24px 28px;
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const Description = styled.p`
    ${pStyle};
    margin: 0;
    color: ${({ theme }) => theme.palette.black};
`;

const ToggleRows = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const ToggleRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    padding: 16px;
    background-color: ${({ theme }) => theme.palette.offWhite};
    border-radius: 6px;
    border: 1px solid ${({ theme }) => theme.palette.blueWhite};
`;

const ToggleLabelGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const ToggleLabel = styled.span`
    font-family: ${({ theme }) => theme.typography.p.fontFamily};
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    font-size: 1.6rem;
    line-height: 1.4;
    color: ${({ theme }) => theme.palette.darkBlue};
`;

const AlwaysActive = styled.span`
    font-family: ${({ theme }) => theme.typography.p.fontFamily};
    font-size: 1.3rem;
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    color: ${({ theme }) => theme.palette.mediumBlue};
`;

const ToggleTrack = styled.button`
    position: relative;
    width: 52px;
    height: 28px;
    border-radius: 14px;
    border: none;
    padding: 0;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    flex-shrink: 0;
    transition: background-color ${({ theme }) => theme.animation.duration.fast}
        ease-in-out;
    background-color: ${({ theme, checked, disabled }) => {
        if (disabled || checked) return theme.palette.mediumBlue;
        return '#9CA3AF';
    }};
    opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};

    &:focus-visible {
        outline: 2px solid ${({ theme }) => theme.palette.mediumBlue};
        outline-offset: 2px;
    }
`;

const ToggleKnob = styled.span`
    position: absolute;
    top: 4px;
    left: ${({ checked }) => (checked ? '28px' : '4px')};
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.palette.white};
    transition: left ${({ theme }) => theme.animation.duration.fast} ease-in-out;
`;

const ModalFooter = styled.div`
    padding: 20px 28px 24px;
    border-top: 1px solid ${({ theme }) => theme.palette.blueWhite};
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 16px;
`;

const FooterButtons = styled.div`
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
`;

const PolicyLink = styled(BaseLink)`
    font-family: ${({ theme }) => theme.typography.p.fontFamily};
    font-size: 1.4rem;
    line-height: 1.4;
    color: ${({ theme }) => theme.palette.darkBlue};
    text-decoration: underline;
    transition: color ${({ theme }) => theme.animation.duration.fast}
        ease-in-out;

    &:hover,
    &:focus {
        color: ${({ theme }) => theme.palette.mediumBlue};
    }
`;

const CookieConsent = () => {
    const { showBanner, marketing, optIn, optOut, closeSettings } =
        useConsentContext();
    const [marketingEnabled, setMarketingEnabled] = useState(marketing);

    useEffect(() => {
        setMarketingEnabled(marketing);
    }, [marketing]);

    const data = useStaticQuery(graphql`
        query CookieConsentPrivacyPage {
            wp {
                siteSettings {
                    siteSettingsFields {
                        cookieConsentText
                        privacyPage {
                            nodes {
                                ... on WpPage {
                                    uri
                                }
                            }
                        }
                    }
                }
            }
        }
    `);

    if (!showBanner) {
        return null;
    }

    const siteSettingsFields = data?.wp?.siteSettings?.siteSettingsFields;
    const cookieConsentText = siteSettingsFields?.cookieConsentText;
    const privacyUri = siteSettingsFields?.privacyPage?.nodes?.[0]?.uri;
    const privacyPageUrl = privacyUri ? getPathFromUrl(privacyUri) : null;

    return (
        <Overlay
            aria-modal="true"
            role="dialog"
            aria-label="Cookie consent preferences"
            onClick={(e) => {
                if (e.target === e.currentTarget) closeSettings();
            }}
        >
            <Modal>
                <ModalHeader>
                    <ModalTitle>Customize Cookie Preferences</ModalTitle>
                    <CloseButton
                        onClick={closeSettings}
                        aria-label="Close cookie preferences"
                    >
                        &times;
                    </CloseButton>
                </ModalHeader>
                <ModalBody>
                    <Description>{cookieConsentText}</Description>
                    <ToggleRows>
                        <ToggleRow>
                            <ToggleLabelGroup>
                                <ToggleLabel>Essential cookies</ToggleLabel>
                                <AlwaysActive>Always active</AlwaysActive>
                            </ToggleLabelGroup>
                            <ToggleTrack
                                checked
                                disabled
                                aria-checked="true"
                                aria-label="Essential cookies"
                                role="switch"
                            >
                                <ToggleKnob checked />
                            </ToggleTrack>
                        </ToggleRow>
                        <ToggleRow>
                            <ToggleLabelGroup>
                                <ToggleLabel>Marketing cookies</ToggleLabel>
                            </ToggleLabelGroup>
                            <ToggleTrack
                                checked={marketingEnabled}
                                onClick={() =>
                                    setMarketingEnabled((prev) => !prev)
                                }
                                aria-checked={marketingEnabled}
                                aria-label="Marketing cookies"
                                role="switch"
                            >
                                <ToggleKnob checked={marketingEnabled} />
                            </ToggleTrack>
                        </ToggleRow>
                    </ToggleRows>
                </ModalBody>
                <ModalFooter>
                    <FooterButtons>
                        <Button
                            buttonType="default"
                            onClick={() => {
                                if (marketingEnabled) {
                                    optIn();
                                } else {
                                    optOut();
                                }
                            }}
                        >
                            Save Preferences
                        </Button>
                    </FooterButtons>
                    {privacyPageUrl && (
                        <PolicyLink to={privacyPageUrl} newTabOverride="newTab">
                            View Privacy Policy
                        </PolicyLink>
                    )}
                </ModalFooter>
            </Modal>
        </Overlay>
    );
};

export default CookieConsent;
